"use client";

import { ClickAwayListener, colors, Grid2 as Grid, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import useTouchable from "@v2/hooks/useTouchable";
import { uiColors } from "@v2/lib/color";
import dayjs from "@v2/lib/dayjs";
import { DeviceViewport } from "@v2/types";
import { ChevronLeft } from "lucide-react";
import { usePathname } from "next/navigation";
import { forwardRef, Fragment, MouseEvent, PointerEvent as ReactPointerEvent, ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import useMountLog from "./useMountLog";

function CardHighlight({ children, sx }: { children: ReactNode, sx?: object }) {
    const cardRef = useRef<HTMLDivElement>(null)
    const hlRef = useRef<HTMLDivElement>(null)
    const active = useRef(false)
    const leaveHlTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

    const fadeOut = () => {
        const hl = hlRef.current
        if (!hl) return
        hl.style.transition = 'opacity 150ms'
        hl.style.opacity = '0'
        active.current = false
    }

    const onOver = (e: React.MouseEvent<HTMLDivElement>) => {
        const hl = hlRef.current
        const card = cardRef.current
        if (!hl || !card) return
        const item = (e.target as HTMLElement).closest('[data-hl]') as HTMLElement
        if (!item) {
            const skip = !!(e.target as HTMLElement).closest('[data-hl-skip]')
            const inZone = !skip && !!(e.target as HTMLElement).closest('[data-hl-zone]')
            if (inZone) {
                clearTimeout(leaveHlTimeout.current)
                leaveHlTimeout.current = undefined
            } else if (active.current && leaveHlTimeout.current === undefined) {
                leaveHlTimeout.current = setTimeout(() => {
                    leaveHlTimeout.current = undefined
                    fadeOut()
                }, 150)
            }
            return
        }
        clearTimeout(leaveHlTimeout.current)
        leaveHlTimeout.current = undefined
        const cr = card.getBoundingClientRect()
        const ir = item.getBoundingClientRect()
        const pad = 2
        const next = {
            top: `${ir.top - cr.top - pad}px`,
            left: `${ir.left - cr.left}px`,
            width: `${ir.width}px`,
            height: `${ir.height + pad * 2}px`,
        }
        if (!active.current) {
            hl.style.transition = 'opacity 180ms'
            Object.assign(hl.style, next)
            hl.style.opacity = '1'
            active.current = true
        } else {
            hl.style.transition = 'top 280ms cubic-bezier(0.16,1,0.3,1), left 280ms cubic-bezier(0.16,1,0.3,1), width 260ms cubic-bezier(0.16,1,0.3,1), height 260ms cubic-bezier(0.16,1,0.3,1)'
            Object.assign(hl.style, next)
        }
    }

    const onLeave = () => {
        clearTimeout(leaveHlTimeout.current)
        leaveHlTimeout.current = undefined
        fadeOut()
    }

    return (
        <Box ref={cardRef} onMouseOver={onOver} onMouseLeave={onLeave} sx={{ position: 'relative', ...sx }}>
            <Box ref={hlRef} sx={{
                position: 'absolute', bgcolor: 'rgba(187,32,46,0.07)',
                borderRadius: '6px', opacity: 0, pointerEvents: 'none', zIndex: 0,
            }} style={{ top: 0, left: 0, width: 100, height: 36 }} />
            {children}
        </Box>
    )
}
import LinkWithoutPrefetch from "../LinkWithoutPrefetch";
import Media from "../Media";
import CenterBox from "../styled/CenterBox";
import { MenuItemType, SubMenuItem } from "./types";
import MediaV2 from "../MediaV2";
import { normalizeMenuPath } from "./utils";

export interface DesktopMenuRef {
    closeSubMenu: () => void,
    subMenuIsOpen: () => boolean
}

interface DesktopMenuProps {
    onChangeSubmenuIsOpen?: (status: boolean) => void,
    isFloating?: boolean,
    data: MenuItemType[],
    viewport: DeviceViewport
}

export default forwardRef<DesktopMenuRef, DesktopMenuProps>(function DesktopMenu(props, ref) {

    useMountLog("DesktopMenu")
    const theme = useTheme()
    const currentPath = normalizeMenuPath(usePathname())
    const menuItemRefs = useRef({})
    const safeZoneWidthsRef = useRef<Record<number, { xs: number, md: number, lg: number, xl: number }>>({})
    const [activeMenu, setActiveMenu] = useState<null | number>(null)
    const [closingKey, setClosingKey] = useState<null | number>(null)
    const [anchorShift, setAnchorShift] = useState(0)

    useImperativeHandle(ref, () => ({
        closeSubMenu: () => {
            closeMenu()
        },
        subMenuIsOpen: () => {
            return activeMenu !== null
        },
    }), [activeMenu])

    // const [enterEffect, setEnterEffect] = useState<'fromOut' | 'fromRight' | 'fromLeft'>('fromOut')
    useEffect(() => {
        props.onChangeSubmenuIsOpen?.(activeMenu !== null)
        activeMenuLive.current = activeMenu;
    }, [activeMenu])

    const leaveTimeout = useRef<NodeJS.Timeout>(undefined)
    const enterTimeout = useRef<NodeJS.Timeout>(undefined)
    const exitTimeout = useRef<NodeJS.Timeout>(undefined)
    const isTouchable = useTouchable()
    const activeMenuLive = useRef<number | null>(null)

    const openMenu = useCallback((menu: number, isHover = false) => {
        clearTimeout(leaveTimeout.current)
        clearTimeout(enterTimeout.current)

        if (activeMenuLive.current === menu) {
            if (!isHover) closeMenu()
            return
        }

        const doOpen = () => {
            clearTimeout(exitTimeout.current)
            setClosingKey(null)
            setActiveMenu(menu)
            const el = menuItemRefs.current[menu] as HTMLElement | undefined
            if (el) {
                const rect = el.getBoundingClientRect()
                const w = window.innerWidth
                const rawShift = (rect.left + rect.width / 2) - w / 2
                const widths = safeZoneWidthsRef.current[menu]
                const safeZoneW = widths
                    ? Math.min(w >= 1536 ? widths.xl : w >= 1200 ? widths.lg : w >= 900 ? widths.md : widths.xs, w)
                    : w
                const maxShift = Math.max(0, Math.min(
                    600 - safeZoneW / 2,
                    (w - safeZoneW) / 2
                ))
                setAnchorShift(Math.max(-maxShift, Math.min(maxShift, rawShift)))
            }
        }

        if (isHover && activeMenuLive.current === null) {
            enterTimeout.current = setTimeout(doOpen, 100)
        } else {
            doOpen()
        }

    }, [activeMenu, isTouchable])

    const closeMenu = useMemo(() => (timeout: number = 0) => {
        clearTimeout(enterTimeout.current)
        clearTimeout(leaveTimeout.current)

        leaveTimeout.current = setTimeout(() => {
            const keyToClose = activeMenuLive.current
            setActiveMenu(null)
            if (keyToClose !== null) {
                setClosingKey(keyToClose)
                exitTimeout.current = setTimeout(() => setClosingKey(null), 80)
            }
        }, timeout)
    }, [activeMenu])

    const onSubMenuMouseEnter = useCallback(() => {
        clearTimeout(leaveTimeout.current)
    }, [])

    const onPointerEnter = useCallback((menu: number) => (event: ReactPointerEvent<HTMLElement>) => {
        if (event.pointerType === 'touch') return
        openMenu(menu, true)
    }, [openMenu])

    const onClickLabel = useCallback((menu: number) => () => {
        if (isTouchable) {
            openMenu(menu)
        } else {
            closeMenu()
        }
    }, [closeMenu, isTouchable, openMenu])

    const closeMenuThenTouch = useCallback(() => {
        closeMenu()
    }, [])

    const onClickSubMenu = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation()
        closeMenu(0)
    }, [])


    const onContainerPointerLeave = useCallback((event: ReactPointerEvent<HTMLElement>) => {
        if (event.pointerType === 'touch') return
        closeMenu(120)
    }, [closeMenu])

    const calcFromNow = useMemo(() => (datetime: string) => {
        return dayjs(datetime, 'YYYY-MM-DD HH:mm:ss').locale('fa').fromNow();
    }, [])


    const renderSubMenu = useMemo(() => (item: SubMenuItem): ReactNode => {
        // red label marks the link of the page we are already on
        const isCurrent = !!item.href && normalizeMenuPath(item.href) === currentPath
        switch (item.type) {
            case "image":
                return (
                    <Grid key={item.id} component="li" size={12} data-hl-skip>
                        <Box component={LinkWithoutPrefetch} href={item.href} onClick={onClickSubMenu} sx={{
                            display: 'block', textDecoration: 'none',
                            borderRadius: 1,
                            '& .image_media_content': {
                                position: 'relative',
                                overflow: 'hidden',
                            },
                            '& .image_media_content::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.28) 0%, transparent 100%)',
                                opacity: 0,
                                transition: 'opacity 320ms ease',
                            },
                            '& .image_label': {
                                transition: 'color 250ms',
                            },
                            '&:hover': {
                                '& .image_media_content::after': {
                                    opacity: 1,
                                },
                                '& .image_label': {
                                    color: uiColors.primary,
                                },
                            }
                        }}>
                            <MediaV2 media_id={item.media_id} alt={item.alt ?? "تصویر"} boxProps={{
                                className: 'image_media_content',
                                sx: {
                                    borderRadius: 1,
                                    aspectRatio: item.ratio ?? "2 / 1"
                                }
                            }}
                            height={224}
                            width={398}
                            options={{
                                quality: 80
                            }}
                            imageProps={{
                                fill: true,
                                style: {
                                    objectFit: 'cover'
                                }
                            }} />
                            {item.label && (
                                <Typography component="span" className="image_label" sx={{
                                    fontSize: 12,
                                    color: isCurrent ? uiColors.primary : colors.grey[700],
                                    mt: 0.3,
                                }}>{item.label}</Typography>
                            )}
                        </Box>
                    </Grid>
                )
            case "brand":
                return (
                    <Grid key={item.id} component="li" size={4} data-hl-skip sx={{ maxHeight: 144 }}>
                        <Box component={LinkWithoutPrefetch} href={item.href} onClick={onClickSubMenu} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textDecoration: 'none',
                            borderRadius: 1,
                            transition: '250ms',
                            height: 95,
                            padding: 1,
                            border: '1px solid',
                            borderColor: colors.grey[300],
                            '&:hover': {
                                borderColor: 'transparent',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.1)',
                                bgcolor: '#fff',
                            }
                        }}>
                            <Media
                                id={item.media_id}
                                alt={item.alt ?? "تصویر برند"}
                                // height={40}
                                width={132}
                                sx={{
                                    maxWidth: 96
                                }}
                                aspectRatio="1.5 / 1"
                                // width={140}
                                // fill
                                // action="thumbnail"
                                background="transparent"
                            />
                        </Box>
                    </Grid>
                )
            case "shape_type":
                return (
                    <Grid key={item.id} component="li" size={2.4} data-hl-skip>
                        <Box component={LinkWithoutPrefetch} href={item.href} onClick={onClickSubMenu} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            textDecoration: 'none',
                            borderRadius: 1,
                            transition: '250ms',
                            height: 92,
                            padding: 1,
                            border: '1px solid',
                            borderColor: colors.grey[300],

                            '&:hover': {
                                borderColor: 'transparent',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.1)',
                                bgcolor: '#fff',
                            }
                        }}>
                            <Media
                                id={item.media_id}
                                aspectRatio="2 / 1"
                                height={40}
                                width={80}
                                fill
                                action="thumbnail"
                                background="transparent"
                                alt={item.label}
                                sx={{ maxWidth: 80 }}
                            />
                            <Typography sx={{ mt: 0.5, color: isCurrent ? uiColors.primary : colors.grey[800], fontSize: 13 }}>{item.label}</Typography>
                        </Box>
                    </Grid>
                )
            case "link":
                return (
                    <Grid key={item.id} component="li" size={12}>
                        <Typography data-hl="true" sx={{
                            color: isCurrent ? uiColors.primary : '#474747',
                            textDecoration: 'none',
                            px: 1,
                            py: 0.75,
                            fontSize: 13,
                            display: 'block',
                            position: 'relative',
                            zIndex: 1,
                            '&:hover': {
                                color: "#BB202E"
                            }
                        }} component={LinkWithoutPrefetch}
                            href={item.href}
                            onClick={onClickSubMenu}>{item.label}</Typography>
                    </Grid>
                )
            case "more_button":
                return (
                    <Grid key={item.id} component="li" size={12}>
                        <Box data-hl="true" sx={{
                            color: item.text_color ?? colors.grey[500],
                            textDecoration: 'none',
                            fontSize: 11,
                            transition: '150ms',
                            py: 0.5,
                            opacity: 0.8,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': {
                                opacity: 1,
                                color: item.text_color ?? uiColors.primary,
                            },
                        }} component={LinkWithoutPrefetch} href={item.href} onClick={onClickSubMenu}>
                            {item.label}
                            <ChevronLeft color="currentColor" size={10} />
                        </Box>
                    </Grid>
                )
            case "blog":
                return (
                    <Grid key={item.id} component="li" size={12} data-hl-skip>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer',
                            '&:hover': {
                                '& p:nth-of-type(1)': {
                                    color: uiColors.primary
                                }
                            },

                        }} component={LinkWithoutPrefetch} href={item.href} onClick={onClickSubMenu}>
                            <Media aspectRatio="2 / 1" width={104} id={item.media_id} alt={item.label} sx={{ borderRadius: '4px', overflow: 'hidden' }} />
                            <Box sx={{ flex: 1, ml: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 400, color: isCurrent ? uiColors.primary : "#212121" }}>{item.label}</Typography>
                                <Typography sx={{ fontSize: 12, color: '#666666', mt: 0.5 }}>{calcFromNow(item.created_at)}</Typography>
                            </Box>
                        </Box>
                    </Grid>
                )
            case "wiki":
                return (
                    <Grid key={item.id} component="li" size={12} data-hl-skip>
                        <Box sx={{
                            display: 'block',
                            textDecoration: 'none',
                            borderLeft: '3px solid',
                            borderColor: colors.grey[300],
                            transition: 'border-color 200ms',
                            pl: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: uiColors.primary,
                                '& p:nth-of-type(1)': {
                                    color: uiColors.primary
                                }
                            },
                        }} component={LinkWithoutPrefetch} href={item.href} onClick={onClickSubMenu}>
                            <Typography sx={{ fontSize: 13, fontWeight: 400, color: isCurrent ? uiColors.primary : "#212121" }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#666666', mt: 0.5 }}>{item.description}</Typography>
                        </Box>
                    </Grid>
                )
            default: return <Fragment>unsupported type</Fragment>
        }
    }, [currentPath])

    return (
        <ClickAwayListener onClickAway={closeMenuThenTouch}>
            <CenterBox onPointerLeave={onContainerPointerLeave} sx={{
                height: 72,
                [theme.breakpoints.down('md2')]: {
                    justifyContent: 'flex-start',
                    height: 56,
                },
            }}>
                <Box component="ul" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: "100%",
                    padding: 0,
                    margin: 0,
                    '& > li': {
                        height: '100%',
                        listStyle: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        '& > .main_label': {
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontWeight: 500,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '8px',
                            position: 'relative',
                            userSelect: 'none',
                            transition: 'background-color 180ms, box-shadow 180ms, transform 100ms',
                            '&:hover': {
                                backgroundColor: '#fff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                            },
                            '&:active': {
                                transform: 'translateY(2px)',
                                boxShadow: 'inset 0 3px 6px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)',
                                transitionDuration: '60ms',
                            }
                        },
                        '&.active > .main_label': {
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                        },
                        '&.current > .main_label': {
                            color: uiColors.primary,
                        },
                    }
                }}>

                    {props.data.map((menuItem, key) => {
                        const classes = [];
                        const isActive = activeMenu === key
                        const isCurrent = normalizeMenuPath(menuItem.href) === currentPath
                        if (isActive) classes.push('active')
                        if (isCurrent) classes.push('current')
                        if (menuItem.children?.length) classes.push('hasChild')
                        const bpOrder = ['xl', 'lg', 'md', 'sm', 'xs'] as const
                        const resolveAt = (col: any, bp: string): number => {
                            const effective = { xs: col.sizes?.md ?? 12, ...col.sizes }
                            const idx = bpOrder.indexOf(bp as any)
                            for (let i = idx; i < bpOrder.length; i++) {
                                const size = (effective as any)[bpOrder[i]]
                                if (typeof size === 'number') return size
                            }
                            return 12
                        }
                        const colTotals = {
                            xs: menuItem.children?.reduce((s, c) => s + resolveAt(c, 'xs'), 0) ?? 12,
                            md: menuItem.children?.reduce((s, c) => s + resolveAt(c, 'md'), 0) ?? 12,
                            lg: menuItem.children?.reduce((s, c) => s + resolveAt(c, 'lg'), 0) ?? 12,
                            xl: menuItem.children?.reduce((s, c) => s + resolveAt(c, 'xl'), 0) ?? 12,
                        }
                        const toMaxWidth = (total: number) => Math.round((Math.min(total, 12) / 12) * 1280)
                        const normalizeSize = (raw: number, total: number) =>
                            total > 0 && total < 12 ? Math.min(12, Math.round((raw / total) * 12)) : raw
                        const submenuMaxWidth = {
                            xs: toMaxWidth(colTotals.xs),
                            md: toMaxWidth(colTotals.md),
                            lg: toMaxWidth(colTotals.lg),
                            xl: toMaxWidth(colTotals.xl),
                        }
                        const safeZoneMaxWidth = {
                            xs: submenuMaxWidth.xs + 64,
                            md: submenuMaxWidth.md + 64,
                            lg: submenuMaxWidth.lg + 64,
                            xl: submenuMaxWidth.xl + 64,
                        }
                        safeZoneWidthsRef.current[key] = safeZoneMaxWidth
                        return (
                            <Box component="li" key={key}
                                ref={(el: any) => menuItemRefs.current[key] = el}
                                className={classes.join(' ')}
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Typography
                                    className="main_label"
                                    component={"span"}
                                    onPointerEnter={onPointerEnter(key)}
                                    sx={{
                                        color: colors.grey[800],
                                        fontSize: 16,
                                        fontWeight: 500
                                    }}
                                    onClick={onClickLabel(key)}
                                    aria-current={isCurrent ? 'page' : undefined}

                                    // menu has children && href is true
                                    {...((menuItem.href && !isTouchable) ? {
                                        component: LinkWithoutPrefetch,
                                        href: menuItem.href
                                    } : {})}
                                >{menuItem.label}</Typography>

                                {menuItem.hasDivider && (
                                    <Box component="span" sx={{
                                        height: 20,
                                        mx: 1,
                                        width: '2px',
                                        bgcolor: colors.grey[300],
                                        display: 'inline-block'
                                    }} />
                                )}

                                {(menuItem.children?.length > 0) && (
                                    <Box
                                        sx={{
                                            position: 'fixed',
                                            top: 64,
                                            [theme.breakpoints.down('md2')]: { top: 104 },
                                            right: 0,
                                            left: 0,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            pointerEvents: activeMenu === key ? 'auto' : 'none',
                                        }}>
                                            <Box
                                                onMouseEnter={onSubMenuMouseEnter}
                                                onPointerLeave={(e: ReactPointerEvent<HTMLElement>) => {
                                                    if (e.pointerType === 'touch') return
                                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                                    if (e.clientY > rect.top) closeMenu(150)
                                                }}
                                                style={{ transform: `translateX(${anchorShift}px)` }}
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: safeZoneMaxWidth,
                                                    px: '32px',
                                                    pb: '32px',
                                                    pt: 0,
                                                }}>
                                            <CardHighlight sx={{
                                                    bgcolor: '#fff',
                                                    width: '100%',
                                                    maxWidth: submenuMaxWidth,
                                                    borderRadius: 1,
                                                    boxShadow: '0 12px 40px rgba(0,0,0,0.13)',
                                                    cursor: 'default',
                                                    overflow: 'hidden',
                                                    opacity: isActive ? 1 : 0,
                                                    transform: isActive
                                                        ? 'translateY(0)'
                                                        : closingKey === key
                                                            ? 'translateY(-8px)'
                                                            : 'translateY(8px)',
                                                    transition: isActive
                                                        ? 'opacity 220ms ease, transform 220ms cubic-bezier(0.22,1,0.36,1)'
                                                        : closingKey === key
                                                            ? 'opacity 80ms ease-in, transform 80ms ease-in'
                                                            : 'none',
                                                }}>
                                                <Grid container component="ul" spacing={2} sx={{
                                                    listStyle: 'none',
                                                    p: 2,
                                                }}>
                                                    {menuItem.children.map((column, key2) => {
                                                        return (
                                                            <Grid key={key2} size={{
                                                                xs: normalizeSize(resolveAt(column, 'xs'), colTotals.xs),
                                                                md: normalizeSize(resolveAt(column, 'md'), colTotals.md),
                                                                lg: normalizeSize(resolveAt(column, 'lg'), colTotals.lg),
                                                                xl: normalizeSize(resolveAt(column, 'xl'), colTotals.xl),
                                                            }} component="li">
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    {column.media_id && (
                                                                        <Box sx={{
                                                                            borderRadius: '50%',
                                                                            border: '1px solid #EBEBEB',
                                                                            mr: 1
                                                                        }}>
                                                                            <Media id={column.media_id} alt={column.label} height={44} width={44} sx={{}} aspectRatio="1 / 1" />
                                                                        </Box>
                                                                    )}
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{column.label}</Typography>
                                                                        {column.description && (
                                                                            <Typography sx={{ color: colors.grey[600], fontSize: 11, mt: '2px' }}>
                                                                                {column.description}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>

                                                                <Grid container component="ul" spacing={1} data-hl-zone sx={{
                                                                    listStyle: 'none',
                                                                    padding: 0, margin: 0, marginTop: 1,
                                                                }}>
                                                                    {column.children.map((item) => renderSubMenu(item))}
                                                                </Grid>
                                                            </Grid>
                                                        )
                                                    })}
                                                </Grid>
                                            </CardHighlight>
                                            </Box>
                                        </Box>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            </CenterBox>
        </ClickAwayListener>
    );
})
